import React, { useState, useCallback } from 'react';
import styles from './PracticeForm.module.css';

export type PracticeCategory =
  | 'pricing'
  | 'scope'
  | 'product'
  | 'installation'
  | 'accounting';

export type PracticePriority = 'critical' | 'standard' | 'nice-to-have';

export interface PracticeFormData {
  title: string;
  description: string;
  category: PracticeCategory;
  subcategory: string;
  conditions: string;
  priority: PracticePriority;
}

export interface PracticeFormProps {
  onSubmit: (data: PracticeFormData) => void;
  onCancel: () => void;
  initialCategory?: PracticeCategory;
  initialData?: Partial<PracticeFormData>;
}

const CATEGORY_OPTIONS: { value: PracticeCategory; label: string }[] = [
  { value: 'pricing', label: 'Pricing' },
  { value: 'scope', label: 'Scope' },
  { value: 'product', label: 'Product' },
  { value: 'installation', label: 'Installation' },
  { value: 'accounting', label: 'Accounting' },
];

const PRIORITY_OPTIONS: { value: PracticePriority; label: string }[] = [
  { value: 'critical', label: 'Critical' },
  { value: 'standard', label: 'Standard' },
  { value: 'nice-to-have', label: 'Nice to Have' },
];

export const PracticeForm: React.FC<PracticeFormProps> = ({
  onSubmit,
  onCancel,
  initialCategory = 'pricing',
  initialData,
}) => {
  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [category, setCategory] = useState<PracticeCategory>(initialData?.category || initialCategory);
  const [subcategory, setSubcategory] = useState(initialData?.subcategory || '');
  const [conditions, setConditions] = useState(initialData?.conditions || '');
  const [priority, setPriority] = useState<PracticePriority>(initialData?.priority || 'standard');
  const [showPreview, setShowPreview] = useState(false);

  const isValid = title.trim().length > 0 && description.trim().length > 0;
  const isEditMode = !!initialData;

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!isValid) return;
      onSubmit({
        title: title.trim(),
        description: description.trim(),
        category,
        subcategory: subcategory.trim(),
        conditions: conditions.trim(),
        priority,
      });
    },
    [isValid, onSubmit, title, description, category, subcategory, conditions, priority]
  );

  const priorityClass = (p: PracticePriority): string => {
    switch (p) {
      case 'critical':
        return styles.priorityCritical;
      case 'standard':
        return styles.priorityStandard;
      case 'nice-to-have':
        return styles.priorityNice;
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.fieldGroup}>
        <label className={styles.label}>
          Title<span className={styles.required}>*</span>
        </label>
        <input
          className={styles.input}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Vessel Sink Rough-In Check"
          autoFocus
        />
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.label}>
          Description<span className={styles.required}>*</span>
        </label>
        <textarea
          className={styles.textarea}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe the rule, best practice, or knowledge..."
        />
      </div>

      <div className={styles.fieldRow}>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Category<span className={styles.required}>*</span>
          </label>
          <select
            className={styles.select}
            value={category}
            onChange={(e) => setCategory(e.target.value as PracticeCategory)}
          >
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>Subcategory</label>
          <input
            className={styles.input}
            type="text"
            value={subcategory}
            onChange={(e) => setSubcategory(e.target.value)}
            placeholder="e.g., tile, plumbing"
          />
        </div>
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.label}>Conditions (Optional)</label>
        <textarea
          className={styles.textarea}
          value={conditions}
          onChange={(e) => setConditions(e.target.value)}
          placeholder="When does this practice apply? e.g., Only for kitchen remodels with vessel sinks"
          rows={2}
        />
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.label}>Priority</label>
        <select
          className={styles.select}
          value={priority}
          onChange={(e) => setPriority(e.target.value as PracticePriority)}
        >
          {PRIORITY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {showPreview && isValid && (
        <div className={styles.preview}>
          <div className={styles.previewHeader}>Practice Preview</div>
          <div className={styles.previewTitle}>{title}</div>
          <div className={styles.previewDescription}>{description}</div>
          <div className={styles.previewMeta}>
            <span className={styles.previewTag}>
              {CATEGORY_OPTIONS.find((o) => o.value === category)?.label}
            </span>
            {subcategory.trim() && (
              <span className={styles.previewTag}>{subcategory}</span>
            )}
            <span className={`${styles.previewTag} ${priorityClass(priority)}`}>
              {PRIORITY_OPTIONS.find((o) => o.value === priority)?.label}
            </span>
            {conditions.trim() && (
              <span className={styles.previewTag}>Conditional</span>
            )}
          </div>
        </div>
      )}

      <div className={styles.actions}>
        <button type="button" className={styles.btnSecondary} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.btnPreview}
          onClick={() => setShowPreview((prev) => !prev)}
          disabled={!isValid}
        >
          {showPreview ? 'Hide Preview' : 'Preview'}
        </button>
        <button type="submit" className={styles.btnPrimary} disabled={!isValid}>
          {isEditMode ? 'Save Changes' : 'Teach Foreman'}
        </button>
      </div>
    </form>
  );
};
